// public/js/services/analysis/AnalysisComponentMethods.js
import { MetricsUtils } from '@/services/insights/competitive/utils/metrics';

export const AnalysisComponentMethods = {
    analyzeWishlistTrends(wishlistData) {
        // Check if wishlistData is valid
        if (!wishlistData || wishlistData.success !== 1 || !wishlistData.wishlistAge) {
            return {
                growth: 0,
                velocity: 0,
                acceleration: 0,
                isAccelerating: false
            };
        }

        // Extract data from wishlistAge
        const wishlistAge = wishlistData.wishlistAge;

        // Define period mapping
        const periodValues = { ">3y": 7, "2-3y": 6, "1-2y": 5, "6m-1y": 4, "3-6m": 3, "1-3m": 2, "<1m": 1 };

        // Sort periods and map to values
        const periods = Object.keys(wishlistAge);
        periods.sort((a, b) => {
            // Normalize keys to handle encoded characters
            const normalizedA = a.replace(/\u003E/g, ">").replace(/\u003C/g, "<");
            const normalizedB = b.replace(/\u003E/g, ">").replace(/\u003C/g, "<");
            return periodValues[normalizedA] - periodValues[normalizedB];
        });

        const values = periods.map(period => Number(wishlistAge[period] || 0));

        // Skip if not enough data
        if (values.length < 2) {
            return {
                growth: 0,
                velocity: 0,
                acceleration: 0,
                isAccelerating: false
            };
        }

        // Process the values array instead of the wishlistData object
        return {
            growth: this.calculateGrowth(values),
            velocity: this.calculateVelocity(values),
            acceleration: this.calculateAcceleration(values),
            isAccelerating: this.calculateAcceleration(values) > 0
        };
    },

    calculateGrowthMetrics(details) {
        const history = details.history || [];
        if (history.length < 2) return null;

        return {
            followerGrowth: this.calculatePercentageChange(
                history[0].followers,
                history[history.length - 1].followers
            ),
            revenueGrowth: this.calculatePercentageChange(
                history[0].revenue,
                history[history.length - 1].revenue
            ),
            trends: {
                isGrowing: history[history.length - 1].revenue > history[0].revenue,
                momentum: this.calculateMomentum(history)
            }
        };
    },

    assessMarketPosition(game, details) {
        return {
            pricePosition: this.analyzePricePosition(game.price, details),
            competitiveAdvantages: this.identifyAdvantages(game, details),
            marketSegment: this.determineMarketSegment(game),
            opportunities: this.identifyOpportunities(game, details)
        };
    },

    analyzePricePosition(price, details) {
        // Add defensive check for undefined details
        if (!details) {
            return {
                tier: this.determinePriceTier(price),
                competitorAverage: 0,
                isCompetitive: true,
                recommendations: [{
                    type: 'info',
                    message: 'No competitor data available for price comparison'
                }]
            };
        }

        // Add defensive check for missing audienceOverlap
        const competitors = details?.audienceOverlap || [];
        const competitorPrices = competitors.map(c => c.price).filter(Boolean);

        return {
            tier: this.determinePriceTier(price),
            competitorAverage: this.calculateAverage(competitorPrices),
            isCompetitive: this.isPriceCompetitive(price, competitorPrices),
            recommendations: this.generatePriceRecommendations(price, competitorPrices)
        };
    },

    identifyAdvantages(game, details) {
        // Add defensive check for undefined details
        if (!details) {
            return {
                advantages: [],
                strengthScore: 0,
                keyDifferentiators: []
            };
        }

        const advantages = [];

        // Use optional chaining to prevent errors
        if (details?.reviewScore > 85) advantages.push('High Rating');
        if (details?.followers > 10000) advantages.push('Strong Following');
        if (this.hasPriceAdvantage(game.price, details)) advantages.push('Competitive Pricing');

        const uniqueFeatures = this.findUniqueFeatures(game, details);
        if (uniqueFeatures.length > 0) advantages.push(...uniqueFeatures);

        return {
            advantages,
            strengthScore: this.calculateStrengthScore(advantages),
            keyDifferentiators: this.findDifferentiators(game, details)
        };
    },

    determineMarketSegment(game) {
        return {
            primary: game.genres?.[0] || 'Unknown',
            secondary: game.genres?.[1] || 'None',
            targetAudience: this.determineTargetAudience(game),
            marketSize: this.estimateMarketSize(game)
        };
    },

    identifyOpportunities(game, details) {
        const opportunities = [];

        if (this.needsMoreContent(game)) {
            opportunities.push('Content Expansion');
        }

        if (this.hasPricingOpportunity(game)) {
            opportunities.push('Price Optimization');
        }

        if (this.hasMarketExpansionPotential(details)) {
            opportunities.push('Market Expansion');
        }

        const featureGaps = this.identifyFeatureGaps(details);
        if (featureGaps.length > 0) {
            opportunities.push(...featureGaps.map(gap => `Add ${gap}`));
        }

        return opportunities;
    },
    generatePriceRecommendations(price, competitorPrices) {
        // Handle edge cases
        if (!competitorPrices || competitorPrices.length === 0) {
            return [{
                type: 'info',
                message: 'No competitor data available for price comparison'
            }];
        }

        const avg = this.calculateAverage(competitorPrices);
        const median = MetricsUtils.calculateMedian(competitorPrices);
        const minPrice = Math.min(...competitorPrices);
        const maxPrice = Math.max(...competitorPrices);
        const recommendations = [];

        // Add recommendation based on median price
        if (price > median * 1.3) {
            recommendations.push({
                type: 'insight',
                message: 'Price is higher than the market median',
                action: 'Your price is in the upper tier of the market',
                data: {
                    currentPrice: price,
                    marketMedian: median
                }
            });
        } else if (price < median * 0.7 && price > 0) {
            recommendations.push({
                type: 'insight',
                message: 'Price is lower than the market median',
                action: 'Your price is in the lower tier of the market',
                data: {
                    currentPrice: price,
                    marketMedian: median
                }
            });
        }

        // Price is significantly higher than competitors
        if (price > avg * 1.2) {
            recommendations.push({
                type: 'warning',
                message: 'Price is significantly higher than competitor average',
                action: 'Consider reducing price to align more closely with market average',
                data: {
                    currentPrice: price,
                    marketAverage: avg,
                    suggestedPrice: Math.round(avg * 1.1)
                }
            });
        }

        // Price is significantly lower than competitors
        if (price < avg * 0.8 && price > 0) {
            recommendations.push({
                type: 'opportunity',
                message: 'Price is significantly lower than competitor average',
                action: 'Potential opportunity to increase price while remaining competitive',
                data: {
                    currentPrice: price,
                    marketAverage: avg,
                    suggestedPrice: Math.round(avg * 0.9)
                }
            });
        }

        // Price is within a competitive range
        if (price >= avg * 0.8 && price <= avg * 1.2) {
            recommendations.push({
                type: 'success',
                message: 'Price is within competitive market range',
                data: {
                    currentPrice: price,
                    marketAverage: avg
                }
            });
        }

        // Price positioning recommendation
        if (price > maxPrice) {
            recommendations.push({
                type: 'info',
                message: 'Premium pricing strategy detected',
                action: 'Ensure product quality and features justify premium price point',
                data: {
                    currentPrice: price,
                    highestCompetitor: maxPrice
                }
            });
        } else if (price < minPrice && price > 0) {
            recommendations.push({
                type: 'info',
                message: 'Value pricing strategy detected',
                action: 'Highlight value proposition in marketing materials',
                data: {
                    currentPrice: price,
                    lowestCompetitor: minPrice
                }
            });
        }

        // Free product recommendation
        if (price === 0) {
            recommendations.push({
                type: 'info',
                message: 'Free product in a paid market',
                action: 'Consider monetization strategies or premium features',
                data: {
                    marketAverage: avg
                }
            });
        }

        return recommendations;
    }
};